import { yupResolver } from "@hookform/resolvers/yup";
import { Typography } from "@mui/material";
import { AuthPageLayout } from "Components/Auth/Shared/AuthPageLayout";
import { StyledContainer } from "Components/Auth/Styles";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { ResetPasswordStep } from "State/Auth/Models/AuthStateModels";
import {
  resetPasswordAsync,
  setPasswordResetStep,
} from "State/Auth/Passwords/ResetPasswordState";
import type { RootStateType } from "State/Store";
import { Resources, useResource } from "Translations/Resources";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { type ObjectSchema, object, string } from "yup";

const COMPONENT_RESOURCES = Resources.Auth.ForgottenPassword;

type FormModel = {
  login: string;
};

const StyledForm = styled.form`
  margin-top: ${(props) => props.theme.spacing(4)};
`;

const StyledSubmitButton = styled(PrimaryButton)`
  margin-top: ${(props) => props.theme.spacing(3)};
`;

const SuccessMessageWrapper = styled.div`
  margin-top: ${(props) => props.theme.spacing(4)};
  margin-bottom: ${(props) => props.theme.spacing(4)};
`;

export const ForgottenPasswordPage = () => {
  const { t } = useResource();
  const dispatch = useDispatch();

  const validationSchema: ObjectSchema<FormModel> = object({
    login: string().required(t(Resources.Validation.Required)),
  }).defined();

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<FormModel>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: { login: "" },
  });

  const onSubmit = (data: FormModel) => {
    dispatch(resetPasswordAsync.request({ login: data.login }));
  };

  const { isLoading, resetPasswordStep } = useSelector(
    (e: RootStateType) => e.auth,
  );

  useEffect(() => {
    return () => {
      dispatch(setPasswordResetStep(ResetPasswordStep.Email));
    };
  }, [dispatch]);

  return (
    <AuthPageLayout
      title={t(COMPONENT_RESOURCES.Title)}
      isLoading={isLoading}
      pageType="other"
    >
      {resetPasswordStep === ResetPasswordStep.Email && (
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <StyledContainer>
            <div>
              <FormInput
                autoFocus
                control={control}
                name="login"
                errors={errors}
                label={t(COMPONENT_RESOURCES.Email)}
                type="email"
                valueTransformer={(v) => v.toLowerCase()}
              />

              <StyledSubmitButton
                type="submit"
                variant="contained"
                color="info"
                isLoading={isLoading}
                fullWidth
              >
                {t(COMPONENT_RESOURCES.ResetPasswordButton)}
              </StyledSubmitButton>
            </div>
          </StyledContainer>
        </StyledForm>
      )}

      {resetPasswordStep === ResetPasswordStep.Success && (
        <StyledContainer>
          <div>
            <SuccessMessageWrapper>
              <Typography>{t(COMPONENT_RESOURCES.SuccessMessage)}</Typography>
            </SuccessMessageWrapper>
          </div>
        </StyledContainer>
      )}
    </AuthPageLayout>
  );
};

export default ForgottenPasswordPage;
